import { useQuery } from '@apollo/client';
import { Edit } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { Link } from '~/components/link';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { DataItemSelectListDocument } from './DataItemSelect.generated';
import { DataItemSelectModal } from './components';
import { FlexEnd, getLabel, useCachedDataItem } from './lib';

export interface DataItemSelectProps {
  dataItemId: number | undefined;
  disabled?: boolean;
  onChange: (dataItemId: number | null) => void;
  optionName: string;
}

export const DataItemSelect = ({
  dataItemId,
  disabled,
  onChange,
  optionName,
}: DataItemSelectProps) => {
  const { t } = useTranslation();
  const dataItem = useCachedDataItem(dataItemId);
  const link = useLink();

  const { currentNetwork } = useAppContext();
  const [open, setOpen] = useState(false);

  const { data: sourcesQuery } = useQuery(DataItemSelectListDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const dataSources = useMemo(() => sourcesQuery?.network?.allDataSources || [], [sourcesQuery]);

  return (
    <>
      {dataItem && (
        <FlexEnd>
          <Link
            color="text.primary"
            target="_blank"
            to={link(`/data-sources/${dataItem.source.id}/data#${dataItem.id}`)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {getLabel(dataItem)}
          </Link>
          <IconButton
            color="secondary"
            label={t('components.data_item_select.change_item')}
            onClick={() => setOpen(() => true)}
            size="small"
          >
            <Edit fontSize="small" />
          </IconButton>
        </FlexEnd>
      )}

      {!dataItem && (
        <Tooltip title={t('components.data_item_select.select_tooltip')}>
          <Button
            disabled={disabled}
            onClick={() => setOpen(() => true)}
            sx={{ alignSelf: 'flex-end' }}
            variant="contained"
          >
            {t('common.actions.select')}
          </Button>
        </Tooltip>
      )}

      {open && (
        <DataItemSelectModal
          dataItem={dataItem}
          dataSources={dataSources}
          onChange={onChange}
          onClose={() => setOpen(() => false)}
          optionName={optionName}
        />
      )}
    </>
  );
};
